import { Controller } from "@hotwired/stimulus";

// Plain-Stimulus port of step2_brand_body.vue + brand_upgrade.vue + offer.vue
// (the step2 branded-fares / fare-family upgrade page). See
// app/views/front/step2/_brand_body.html.erb for why every cabin's offer
// cards are rendered upfront (hidden via CSS) instead of re-rendered on
// cabin switch like the Vue original did.
export default class extends Controller {
  static targets = [
    "cabinState",
    "offerCard",
    "bagRow",
    "bagCheckbox",
    "ctaButton",
    "ctaLabel",
    "ctaPrice",
    "actionAlert",
    "actionAlertClass",
    "submitButton",
    "submitSpinner",
    "errorModal",
  ];
  static values = { checkUrl: String };

  connect() {
    this.selectedCard = this.element.querySelector(
      '[data-branded-fares-target="offerCard"].border-primary'
    );
    const initial = this.currentCabinState();
    this.originalCabinCode = initial ? initial.dataset.cabinCode : null;
    // Same "read whatever currency suffix the server already rendered"
    // approach as step2_price_controller.js -- this page serves multiple
    // gates/currencies.
    if (this.selectedCard) {
      const priceEl = this.selectedCard.querySelector('[data-branded-fares-target="ctaPrice"]');
      const match = priceEl && priceEl.textContent.trim().match(/[^\d\s.,]+$/);
      this.currencySuffix = match ? ` ${match[0]}` : "";
    } else {
      this.currencySuffix = "";
    }
  }

  currentCabinState() {
    return this.cabinStateTargets.find((el) => !el.classList.contains("hidden"));
  }

  selectOffer(event) {
    const card = event.currentTarget;
    if (card === this.selectedCard) return;

    const cabinState = card.closest('[data-branded-fares-target="cabinState"]');
    if (!cabinState) return;

    cabinState.querySelectorAll('[data-branded-fares-target="offerCard"]').forEach((other) => {
      this.markCardSelected(other, other === card);
    });
    this.selectedCard = card;
  }

  markCardSelected(card, isSelected) {
    card.classList.toggle("border-primary", isSelected);
    card.classList.toggle("ring-2", isSelected);
    card.classList.toggle("ring-primary", isSelected);
    card.classList.toggle("border-gray-200", !isSelected);
    card.classList.toggle("hover:border-gray-300", !isSelected);

    const ctaButton = card.querySelector('[data-branded-fares-target="ctaButton"]');
    if (ctaButton) {
      ctaButton.classList.toggle("bg-accent", isSelected);
      ctaButton.classList.toggle("text-gray-900", isSelected);
      ctaButton.classList.toggle("hover:brightness-95", isSelected);
      ctaButton.classList.toggle("bg-primary", !isSelected);
      ctaButton.classList.toggle("text-white", !isSelected);
      ctaButton.classList.toggle("hover:brightness-110", !isSelected);
    }

    const ctaLabel = card.querySelector('[data-branded-fares-target="ctaLabel"]');
    if (ctaLabel) {
      ctaLabel.textContent = isSelected ? ctaLabel.dataset.selectedLabel : ctaLabel.dataset.selectLabel;
    }

    card.querySelectorAll('[data-branded-fares-target="bagRow"]').forEach((row) => {
      row.classList.toggle("opacity-50", !isSelected);
    });

    card.querySelectorAll('[data-branded-fares-target="bagCheckbox"]').forEach((checkbox) => {
      if (!isSelected) {
        checkbox.checked = false;
        checkbox.disabled = true;
      } else {
        checkbox.disabled = checkbox.dataset.itemDisabled === "true";
      }
    });

    this.recalcCardPrice(card);
  }

  toggleBag(event) {
    const card = event.target.closest('[data-branded-fares-target="offerCard"]');
    if (card) this.recalcCardPrice(card);
  }

  recalcCardPrice(card) {
    const basePrice = Number(card.dataset.basePrice) || 0;
    let total = basePrice;

    card.querySelectorAll('[data-branded-fares-target="bagCheckbox"]').forEach((checkbox) => {
      if (checkbox.checked) total += Number(checkbox.dataset.price) || 0;
    });

    const priceEl = card.querySelector('[data-branded-fares-target="ctaPrice"]');
    if (priceEl) priceEl.textContent = this.formatPrice(total);
  }

  formatPrice(amount) {
    return `${Math.round(amount).toLocaleString("sv-SE")}${this.currencySuffix || ""}`;
  }

  // Cabin switching always jumps to the target cabin's first offer,
  // regardless of which cabin was previously active -- matches the Vue
  // original's `watch: { selectedClass() { ... firstOfferItem() } }`,
  // which fired on every change including switching back to the cabin you
  // started on (no "remembered" per-cabin selection).
  switchCabin(event) {
    const button = event.currentTarget;
    const index = Number(button.dataset.cabinIndex);
    const targetState = this.cabinStateTargets[index];
    if (!targetState) return;

    this.cabinStateTargets.forEach((state) => state.classList.add("hidden"));
    targetState.classList.remove("hidden");

    const cards = targetState.querySelectorAll('[data-branded-fares-target="offerCard"]');
    cards.forEach((card, i) => this.markCardSelected(card, i === 0));
    this.selectedCard = cards[0] || null;

    this.setAlert(targetState.dataset.cabinCode !== this.originalCabinCode, button.dataset.cabinTitle);
  }

  cancelUpgrade() {
    const originalIndex = this.cabinStateTargets.findIndex(
      (state) => state.dataset.cabinCode === this.originalCabinCode
    );
    if (originalIndex === -1) return;

    this.cabinStateTargets.forEach((state) => state.classList.add("hidden"));
    const targetState = this.cabinStateTargets[originalIndex];
    targetState.classList.remove("hidden");

    const cards = targetState.querySelectorAll('[data-branded-fares-target="offerCard"]');
    cards.forEach((card, i) => this.markCardSelected(card, i === 0));
    this.selectedCard = cards[0] || null;

    this.setAlert(false);
  }

  setAlert(show, title) {
    if (!this.hasActionAlertTarget) return;
    this.actionAlertTarget.classList.toggle("hidden", !show);
    this.actionAlertTarget.classList.toggle("flex", show);
    if (show && this.hasActionAlertClassTarget) {
      this.actionAlertClassTarget.textContent = title || "";
    }
  }

  submit() {
    if (!this.selectedCard) return;

    this.submitButtonTarget.disabled = true;
    if (this.hasSubmitSpinnerTarget) this.submitSpinnerTarget.classList.remove("hidden");

    const uuid = this.selectedCard.dataset.offerUuid;
    const seatsIncluded = this.selectedCard.dataset.seatsIncluded === "true";

    const baggage = { [uuid]: {} };
    this.selectedCard.querySelectorAll('[data-branded-fares-target="bagCheckbox"]').forEach((checkbox) => {
      if (checkbox.checked) {
        const key = `${checkbox.dataset.paxNo}_${checkbox.dataset.segIndex}_BAG1_1PC23`;
        baggage[uuid][key] = "1";
      }
    });

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    fetch(this.checkUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken || "",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ uuid, baggage, branded_fares: true, seats_included: seatsIncluded }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.available) {
          window.location.href = data.url;
        } else {
          this.showError();
        }
      })
      .catch(() => this.showError());
  }

  showError() {
    this.submitButtonTarget.disabled = false;
    if (this.hasSubmitSpinnerTarget) this.submitSpinnerTarget.classList.add("hidden");
    if (this.hasErrorModalTarget) {
      this.errorModalTarget.classList.remove("hidden");
      this.errorModalTarget.classList.add("flex");
    }
  }

  closeError() {
    if (this.hasErrorModalTarget) {
      this.errorModalTarget.classList.add("hidden");
      this.errorModalTarget.classList.remove("flex");
    }
  }

  // Wired on the modal's own backdrop -- event.currentTarget is that
  // backdrop, event.target is whatever was actually clicked, equal only
  // when the click didn't bubble up from the card content inside it.
  closeErrorOnBackdrop(event) {
    if (event.target === event.currentTarget) this.closeError();
  }
}
